import React, { useState } from "react";
import PDA1131_R01Store from "./PDA1131_R01Store";
import DateRangeInput from "src/components/common/DateRangeInput";
import { useBarcodeScan } from "src/hooks/useBarcodeScan";

/** 검색 영역 props */
interface PDA1131_R01SearchProps {
  onSearch: () => void;
}

/** 출하지시 목록 검색 — 지시번호 스캔·기간·조회 */
const PDA1131_R01Search: React.FC<PDA1131_R01SearchProps> = ({ onSearch }) => {
  const { searchParams, updateSearchParam } = PDA1131_R01Store();
  /** PDA 소프트키보드 표시 여부 */
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  /** 더블탭 감지용 마지막 터치 시각(ms) */
  const [lastTapTime, setLastTapTime] = useState(0);

  /** 출하지시 바코드 스캔 완료 → orderNo 반영 후 즉시 조회 */
  const handleOrderNoScan = (scannedValue: string) => {
    updateSearchParam("orderNo", scannedValue);
    onSearch();
  };

  /** 바코드 스캔 훅 (출하지시번호 입력란) */
  const { scanInputRef, handleScanKeyDown, handleInput } = useBarcodeScan(
    handleOrderNoScan,
    {
      clearOnScan: false,
      refocusOnScan: true,
      debounceMs: 100,
      autoFocus: true,
    }
  );

  /** 입력란 더블탭 시 소프트키보드 활성화 */
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapTime < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }
    setLastTapTime(now);
  };

  /** 키보드 표시/숨김 토글 */
  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  return (
    <div className="w-full bg-white p-4 pb-1 border-b border-gray-200">
      <div className="space-y-3">
        {/* 출하지시 스캔 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">
            출하지시
          </label>
          <div className="flex-1 flex gap-1">
            <input
              ref={scanInputRef}
              type="text"
              value={searchParams.orderNo || ""}
              onChange={(e) => updateSearchParam("orderNo", e.target.value)}
              onInput={handleInput}
              onKeyDown={handleScanKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              autoComplete="off"
              className="flex-1 max-w-[215px] px-2 py-0.5 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="출하지시번호 스캔"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              className={`w-10 flex-shrink-0 px-2 py-0.5 border border-gray-300 rounded transition-colors ${
                keyboardEnabled
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* 기간 */}
        <DateRangeInput
          startDate={searchParams.startDate}
          endDate={searchParams.endDate}
          onStartDateChange={(date) => updateSearchParam("startDate", date)}
          onEndDateChange={(date) => updateSearchParam("endDate", date)}
          label="출하기간"
          labelWidth="w-20"
        />

        {/* 조회 버튼 */}
        <button
          onClick={onSearch}
          className="w-full bg-blue-500 text-white px-4 py-2 rounded text-lg font-medium hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          조회
        </button>
      </div>
    </div>
  );
};

export default PDA1131_R01Search;
